// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

const API_BASE = process.env.REACT_APP_API_URL || 'https://mcnallaz.com:8001';

// ─── CSRF token helper ────────────────────────────────────────────────────────
// Backend sets a non-HttpOnly `app_csrf` cookie on login/refresh. We read it
// from document.cookie and echo it back in X-CSRF-Token on state-changing
// requests so the double-submit-cookie middleware can verify the request
// originated from this page (not a cross-site forgery).
const _csrfFromCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)app_csrf=([^;]+)/);
  return match ? match[1] : '';
};

const _isMutating = (method) =>
  !['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase());

// ─── AbortController registry ─────────────────────────────────────────────────
// Calling getSignal('key') cancels any previous in-flight request for that key.
const _controllers = {};
const getSignal = (key) => {
  if (_controllers[key]) _controllers[key].abort();
  _controllers[key] = new AbortController();
  return _controllers[key].signal;
};

// ─── Token refresh (deduplicated) ─────────────────────────────────────────────
// Multiple concurrent 401s share a single refresh attempt rather than
// hammering the /refresh endpoint. Once resolved, all callers get the
// same new token.
let _refreshPromise = null;

async function attemptRefresh() {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    try {
      // Send the stored refresh token in the body — works cross-origin in dev
      // (where SameSite=Lax cookies aren't sent). In production the HttpOnly
      // cookie is also sent automatically via credentials:'include'.
      const storedRefresh = sessionStorage.getItem('auth_refresh');
      const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefresh || null })
      });

      if (!response.ok) return null;

      const data = await response.json();
      const newToken = data.access_token;
      const newRefresh = data.refresh_token;

      if (newToken) {
        sessionStorage.setItem('auth_token', newToken);
        if (newRefresh) sessionStorage.setItem('auth_refresh', newRefresh);
        // Notify AuthContext so it updates its in-memory token state
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token: newToken } }));
      }

      return newToken || null;
    } catch {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

function _signalExpired() {
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_refresh');
  sessionStorage.removeItem('auth_user');
  window.dispatchEvent(new CustomEvent('auth:expired'));
}

// ─── Core fetch wrapper ────────────────────────────────────────────────────────
// On 401: silently refresh the token and retry the original request once.
// On second 401: give up and signal expiry to the auth system.
// Also injects X-CSRF-Token on mutating methods so the backend's CSRF
// middleware (double-submit-cookie) accepts the request.
async function apiFetch(url, options = {}) {
  // Inject CSRF token on mutating methods. No-op for Bearer-only requests
  // (the backend bypasses CSRF when an Authorization header is present)
  // but harmless to always include.
  if (_isMutating(options.method)) {
    const csrf = _csrfFromCookie();
    if (csrf) {
      options.headers = { ...options.headers, 'X-CSRF-Token': csrf };
    }
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    const newToken = await attemptRefresh();

    if (newToken) {
      // Retry without the original AbortController signal (signals are one-use)
      const { signal: _dropped, ...retryOptions } = options;
      // Re-read CSRF — refresh may have rotated it
      const retryHeaders = {
        ...retryOptions.headers,
        'Authorization': `Bearer ${newToken}`,
      };
      if (_isMutating(retryOptions.method)) {
        const csrf = _csrfFromCookie();
        if (csrf) retryHeaders['X-CSRF-Token'] = csrf;
      }
      const retryResponse = await fetch(url, {
        ...retryOptions,
        headers: retryHeaders,
      });

      if (retryResponse.status === 401) {
        _signalExpired();
        return;
      }

      return _handleResponse(retryResponse);
    }

    _signalExpired();
    return;
  }

  return _handleResponse(response);
}

// ─── Response handler ─────────────────────────────────────────────────────────
async function _handleResponse(response) {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: 'Request failed' }));
    const message = (typeof body.detail === 'object' ? body.detail?.message : body.detail)
      || body.error
      || 'Request failed';
    const err = new Error(message);
    err.status = response.status;
    err.data = body.detail;
    throw err;
  }
  return response.json();
}

// ─── Shared auth headers ──────────────────────────────────────────────────────
const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
});

// ─── Public API ───────────────────────────────────────────────────────────────
export const api = {
  auth: {
    register: async (username, email, password) =>
      apiFetch(`${API_BASE}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, email, password })
      }),

    login: async (email, password) =>
      apiFetch(`${API_BASE}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      }),

    logout: async () =>
      apiFetch(`${API_BASE}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }),

    me: async (token) =>
      apiFetch(`${API_BASE}/api/v1/auth/me`, {
        headers: authHeaders(token),
        credentials: 'include'
      }),

    changePassword: async (token, oldPassword, newPassword) =>
      apiFetch(`${API_BASE}/api/v1/auth/password`, {
        method: 'PUT',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
      })
  },

  game: {
    saveGame: async (token, saveName, gameState, clientSaveId = null) => {
      const body = { save_name: saveName, game_state: gameState };
      if (clientSaveId) body.last_known_save_id = clientSaveId;
      return apiFetch(`${API_BASE}/api/v1/game/save`, {
        method: 'POST',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify(body),
        signal: getSignal('saveGame')
      });
    },

    loadGame: async (token) =>
      apiFetch(`${API_BASE}/api/v1/game/save`, {
        headers: authHeaders(token),
        credentials: 'include',
        signal: getSignal('loadGame')
      }),

    listSaves: async (token) =>
      apiFetch(`${API_BASE}/api/v1/game/saves`, {
        headers: authHeaders(token),
        credentials: 'include'
      }),

    getSettings: async (token) =>
      apiFetch(`${API_BASE}/api/v1/game/settings`, {
        headers: authHeaders(token),
        credentials: 'include'
      }),

    updateSettings: async (token, settings) =>
      apiFetch(`${API_BASE}/api/v1/game/settings`, {
        method: 'PUT',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify({ settings })
      })
  },

  kb: {
    query: async (token, kb, text, top_k = 5) =>
      apiFetch(`${API_BASE}/api/v1/kb/query`, {
        method: 'POST',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify({ kb, text, top_k })
      })
  },

  tts: {
    speak: async (token, text, voice = null) => {
      const response = await fetch(`${API_BASE}/api/v1/kb/tts/speak`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text, voice })
      });
      if (!response.ok) throw new Error(`TTS ${response.status}`);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  },

  admin: {
    listUsers: async (token) =>
      apiFetch(`${API_BASE}/api/v1/admin/users`, {
        headers: authHeaders(token),
        credentials: 'include'
      }),

    updateUser: async (token, userId, updates) =>
      apiFetch(`${API_BASE}/api/v1/admin/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify(updates)
      }),

    resetPassword: async (token, userId, newPassword) =>
      apiFetch(`${API_BASE}/api/v1/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: authHeaders(token),
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword })
      })
  }
};
